import superagent from 'superagent';
import * as types from './types';
import { forbiddenAccess } from './auth';
import utils from '../lib/utils';

const request = superagent
  .agent()
  .set('ETag', window.Config.APP_VERSION)
  .use(function (req) {
    req.on('response', function (res) {
      if (res.status === 401) {
        console.log('UnAuthorized');
        return {
          type: types.LOGOUT_USER,
        };
      } else if (res.status === 403) {
        console.log('Forbidden');
        forbiddenAccess();
      }
    });
  });

const API_BASE_URL = window.Config ? window.Config.API_URL || '/api' : '/api';
const DOCUMENTS_ENDPOINT = `${API_BASE_URL}/ai_assists/documents`;
const CATEGORIES_ENDPOINT = `${API_BASE_URL}/ai_assists/categories`;
const INVALIDATE_ENDPOINT = `${API_BASE_URL}/ai_assists/documents/invalidate`;

function mapApiResponse(data) {
  if (Array.isArray(data)) {
    return data.map((item) => ({
      id: item.new_solr_id || item.id || item.document_id,
      _originalId: item.id,
      _documentId: item.document_id,
      _newSolrId: item.new_solr_id,
      UserMessage: item.context || item.user_message || item.UserMessage || '',
      Response: item.response || item.Response || '',
      ExpiryDate: ((item.expiry_date || item.ExpiryDate) + '').toString(),
      Categories: Array.isArray(item.category)
        ? item.category
        : (Array.isArray(item.categories) ? item.categories
          : (item.category ? [item.category]
            : (Array.isArray(item.Categories) ? item.Categories : []))),
      updatedBy: item.updatedBy || item.updated_by || item.UpdatedBy || null,
      updatedOn: item.updatedOn || item.updated_on || item.UpdatedOn || null,
    }));
  }
  return data;
}

function formatSingleDocumentForAPI(config) {
  return {
    context: config.UserMessage || config.Context || '',
    response: config.Response || '',
    category: Array.isArray(config.Categories) && config.Categories.length > 0
      ? config.Categories
      : (typeof config.Categories === 'string' ? [config.Categories] : []),
    expiryDate: config.ExpiryDate,
  };
}

function formatBulkDocumentsForAPI(configurations) {
  return {
    dataList: configurations.map((config) => formatSingleDocumentForAPI(config)),
    bzIds: [utils.getCookie('studio_id')],
  };
}

export function getConfigurationsStart() {
  return { type: types.GET_CONFIGS_START };
}

export function getConfigurationsSuccess({ configurations }) {
  return { type: types.GET_CONFIGS_SUCCESS, configurations };
}

export function getConfigurationsFail(error) {
  return { type: types.GET_CONFIGS_FAIL, error };
}

export function addConfigurationStart() {
  return { type: types.ADD_CONFIG_START };
}

export function addConfigurationSuccess({ configuration }) {
  return { type: types.ADD_CONFIG_SUCCESS, configuration };
}

export function addConfigurationFail(error) {
  return { type: types.ADD_CONFIG_FAIL, error };
}

export function updateConfigurationStart() {
  return { type: types.UPDATE_CONFIG_START };
}

export function updateConfigurationSuccess({ configuration }) {
  return { type: types.UPDATE_CONFIG_SUCCESS, configuration };
}

export function updateConfigurationFail(error) {
  return { type: types.UPDATE_CONFIG_FAIL, error };
}

export function deleteConfigurationStart() {
  return { type: types.DELETE_CONFIG_START };
}

export function deleteConfigurationSuccess({ id }) {
  return { type: types.DELETE_CONFIG_SUCCESS, id };
}

export function deleteConfigurationFail(error) {
  return { type: types.DELETE_CONFIG_FAIL, error };
}

export function bulkDeleteStart() {
  return { type: types.BULK_DELETE_START };
}

export function bulkDeleteSuccess(deletedIds) {
  return { type: types.BULK_DELETE_SUCCESS, deletedIds };
}

export function bulkDeleteFail(error) {
  return { type: types.BULK_DELETE_FAIL, error };
}

export function searchConfigurationsStart() {
  return { type: types.SEARCH_CONFIGS_START };
}

export function searchConfigurationsSuccess({ configurations }) {
  return { type: types.SEARCH_CONFIGS_SUCCESS, configurations };
}

export function searchConfigurationsFail(error) {
  return { type: types.SEARCH_CONFIGS_FAIL, error };
}

export function selectConfiguration({ configuration }) {
  return { type: types.SELECT_CONFIG, configuration };
}

export function clearError() {
  return { type: types.CLEAR_ERROR };
}

export function bulkAddStart() {
  return { type: types.BULK_ADD_START };
}

export function bulkAddSuccess(configurations) {
  return { type: types.BULK_ADD_SUCCESS, configurations };
}

export function bulkAddFail(error) {
  return { type: types.BULK_ADD_FAIL, error };
}

export function getConfigurations() {
  return async (dispatch) => {
    dispatch(getConfigurationsStart());

    try {
      const businessId = utils.getCookie('studio_id');
      const queryParams = {
        business_id: businessId,
        text: '*:*',
      };
      const response = await request
        .get(DOCUMENTS_ENDPOINT)
        .query(queryParams)
        .withCredentials();

      let configurations = [];
      if (response.body && Array.isArray(response.body)) {
        configurations = mapApiResponse(response.body);
      } else if (response.body?.results && Array.isArray(response.body.results)) {
        configurations = mapApiResponse(response.body.results);
      } else if (response.body?.data && Array.isArray(response.body.data)) {
        configurations = mapApiResponse(response.body.data);
      }

      dispatch(getConfigurationsSuccess({ configurations }));
      return configurations;
    } catch (error) {
      console.error('Failed to load configurations:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseBody: error.response?.body,
        responseText: error.response?.text,
      });

      const errorMessage = error.response?.body?.message ||
                          error.response?.body?.error ||
                          error.response?.text ||
                          error.message ||
                          'Failed to load configurations';

      dispatch(getConfigurationsFail(errorMessage));
      throw error;
    }
  };
}

export function addConfiguration(configData, bzIds) {
  return async (dispatch) => {
    dispatch(addConfigurationStart());

    try {
      if (Array.isArray(configData)) {
        return dispatch(bulkAddConfigurations(configData));
      }

      const formattedPayload = formatBulkDocumentsForAPI([configData]);

      const response = await request
        .post(DOCUMENTS_ENDPOINT)
        .set('Content-Type', 'application/json')
        .set('Accept', 'application/json')
        .withCredentials()
        .buffer(true)
        .parse(request.parse.json)
        .serialize(request.serialize.json)
        .send(formattedPayload)
        .ok((res) => res.status >= 200 && res.status < 400);

      let configuration = {};
      if (response.body) {
        if (Array.isArray(response.body)) {
          configuration = mapApiResponse(response.body)[0] || {};
        } else if (response.body.data && Array.isArray(response.body.data)) {
          configuration = mapApiResponse(response.body.data)[0] || {};
        } else if (response.body.results && Array.isArray(response.body.results)) {
          configuration = mapApiResponse(response.body.results)[0] || {};
        } else {
          configuration = mapApiResponse([response.body])[0] || {};
        }
      } else {
        configuration = {
          id: `temp_${Date.now()}`,
          UserMessage: configData.UserMessage,
          Response: configData.Response,
          expiryDate: configData.ExpiryDate,
          Categories: configData.Categories,
        };
      }

      dispatch(addConfigurationSuccess({ configuration }));

      try {
        dispatch(getConfigurations());
      } catch (refreshError) {
        console.warn('Failed to refresh configurations after add:', refreshError);
      }

      return configuration;
    } catch (error) {
      console.error('Error adding configuration:', error);
      const errorMessage = error.response?.body?.message ||
                          error.response?.body?.error ||
                          error.response?.text ||
                          error.message ||
                          'Failed to add configuration';

      dispatch(addConfigurationFail(errorMessage));
      throw error;
    }
  };
}

export function bulkAddConfigurations(configurations) {
  return async (dispatch) => {
    dispatch(bulkAddStart());

    try {
      if (!Array.isArray(configurations) || configurations.length === 0) {
        throw new Error('Invalid input: configurations must be a non-empty array');
      }
      const formattedPayload = formatBulkDocumentsForAPI(configurations);
      const response = await request
        .post(DOCUMENTS_ENDPOINT)
        .set('Content-Type', 'application/json')
        .withCredentials()
        .send(formattedPayload);

      let results = [];
      if (response.body) {
        if (Array.isArray(response.body)) {
          results = mapApiResponse(response.body);
        } else if (response.body.data && Array.isArray(response.body.data)) {
          results = mapApiResponse(response.body.data);
        } else if (response.body.results && Array.isArray(response.body.results)) {
          results = mapApiResponse(response.body.results);
        } else {
          results = [response.body];
        }
      }

      dispatch(bulkAddSuccess(results));

      try {
        dispatch(getConfigurations());
      } catch (refreshError) {
        console.warn('Failed to refresh configurations after bulk add:', refreshError);
      }

      return results;
    } catch (error) {
      console.error('Error bulk adding configurations:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseBody: error.response?.body,
        responseText: error.response?.text,
      });

      const errorMessage = error.response?.body?.message ||
                          error.response?.body?.error ||
                          error.response?.text ||
                          error.message ||
                          'Failed to add configurations';

      dispatch(bulkAddFail(errorMessage));
      throw error;
    }
  };
}

export function updateConfiguration(configData) {
  return async (dispatch) => {
    dispatch(updateConfigurationStart());

    try {
      if (!configData.id) {
        throw new Error('Configuration ID is required for update');
      }

      let oldDocumentId = configData.id;
      if (configData._documentId) {
        oldDocumentId = configData._documentId;
      } else if (configData._newSolrId) {
        oldDocumentId = configData._newSolrId;
      } else if (configData._originalId) {
        oldDocumentId = configData._originalId;
      }

      const updatePayload = {
        business_id: utils.getCookie('studio_id'),
        old_document_id: oldDocumentId,
        new_document: configData,
      };

      const response = await request
        .patch(DOCUMENTS_ENDPOINT)
        .set('Content-Type', 'application/json')
        .withCredentials()
        .send(updatePayload);

      let configuration = {};
      if (response.body) {
        if (Array.isArray(response.body)) {
          configuration = mapApiResponse(response.body)[0] || configData;
        } else if (response.body.data && Array.isArray(response.body.data)) {
          configuration = mapApiResponse(response.body.data)[0] || configData;
        } else if (response.body.results && Array.isArray(response.body.results)) {
          configuration = mapApiResponse(response.body.results)[0] || configData;
        } else {
          configuration = mapApiResponse([response.body])[0] || configData;
        }
      } else {
        configuration = configData;
      }

      dispatch(updateConfigurationSuccess({ configuration }));

      try {
        dispatch(getConfigurations());
      } catch (refreshError) {
        console.warn('Failed to refresh configurations after update:', refreshError);
      }

      return configuration;
    } catch (error) {
      console.error('Error updating configuration:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseBody: error.response?.body,
        responseText: error.response?.text,
      });

      const errorMessage = error.response?.body?.message ||
                          error.response?.body?.error ||
                          error.response?.text ||
                          error.message ||
                          'Failed to update configuration';

      dispatch(updateConfigurationFail(errorMessage));
      throw error;
    }
  };
}

export function deleteConfiguration(configToDelete) {
  return async (dispatch) => {
    dispatch(deleteConfigurationStart());

    try {
      let documentId = configToDelete.id;
      if (configToDelete._documentId) {
        documentId = configToDelete._documentId;
      } else if (configToDelete._newSolrId) {
        documentId = configToDelete._newSolrId;
      } else if (configToDelete._originalId) {
        documentId = configToDelete._originalId;
      }

      if (!documentId) {
        throw new Error('No valid document ID found for deletion');
      }

      const deletePayload = {
        business_id: utils.getCookie('studio_id'),
        document_id: [documentId],
      };

      const response = await request
        .put(INVALIDATE_ENDPOINT)
        .set('Content-Type', 'application/json')
        .withCredentials()
        .send(deletePayload);
      if (response.body && response.body.error) {
        throw new Error(response.body.error);
      }

      dispatch(deleteConfigurationSuccess({ id: configToDelete.id }));

      try {
        await dispatch(getConfigurations());
      } catch (refreshError) {
        console.warn('Failed to refresh configurations after delete:', refreshError);
      }

      return configToDelete.id;
    } catch (error) {
      console.error('=== DELETE ERROR ===');
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseBody: error.response?.body,
        responseText: error.response?.text,
      });

      const errorMessage = error.response?.body?.message ||
                          error.response?.body?.error ||
                          error.response?.text ||
                          error.message ||
                          'Failed to delete configuration';

      dispatch(deleteConfigurationFail(errorMessage));
      throw error;
    }
  };
}

export function bulkDeleteConfigurations(configurationsToDelete) {
  return async (dispatch) => {
    dispatch(bulkDeleteStart());

    try {
      if (!Array.isArray(configurationsToDelete) || configurationsToDelete.length === 0) {
        throw new Error('No configurations selected for deletion');
      }

      const documentIds = configurationsToDelete.map((config) => {
        let documentId = config.id;
        if (config._documentId) {
          documentId = config._documentId;
        } else if (config._newSolrId) {
          documentId = config._newSolrId;
        } else if (config._originalId) {
          documentId = config._originalId;
        }

        if (!documentId) {
          console.warn('No valid document ID found for config:', config);
        }

        return documentId;
      }).filter((id) => id);

      if (documentIds.length === 0) {
        throw new Error('No valid document IDs found for deletion');
      }

      const deletePayload = {
        business_id: utils.getCookie('studio_id'),
        document_id: documentIds,
      };
      const response = await request
        .put(INVALIDATE_ENDPOINT)
        .set('Content-Type', 'application/json')
        .withCredentials()
        .send(deletePayload);

      if (response.body && response.body.error) {
        throw new Error(response.body.error);
      }

      const originalIds = configurationsToDelete.map((config) => config.id);

      dispatch(bulkDeleteSuccess(originalIds));

      try {
        await dispatch(getConfigurations());
      } catch (refreshError) {
        console.warn('Failed to refresh configurations after bulk delete:', refreshError);
      }

      return originalIds;
    } catch (error) {
      console.error('=== BULK DELETE ERROR ===');
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseBody: error.response?.body,
        responseText: error.response?.text,
      });

      const errorMessage = error.response?.body?.message ||
                          error.response?.body?.error ||
                          error.response?.text ||
                          error.message ||
                          'Failed to delete configurations';

      dispatch(bulkDeleteFail(errorMessage));
      throw error;
    }
  };
}

export function searchConfigurations(searchTerm) {
  return async (dispatch) => {
    dispatch(searchConfigurationsStart());

    try {
      const businessId = utils.getCookie('studio_id');
      const queryText = searchTerm.trim() ? `*${searchTerm.trim()}*` : '*:*';
      const queryParams = {
        business_id: businessId,
        text: queryText,
      };
      const response = await request
        .get(DOCUMENTS_ENDPOINT)
        .query(queryParams)
        .withCredentials();

      let configurations = [];
      if (response.body && Array.isArray(response.body)) {
        configurations = mapApiResponse(response.body);
      } else if (response.body?.results && Array.isArray(response.body.results)) {
        configurations = mapApiResponse(response.body.results);
      } else if (response.body?.data && Array.isArray(response.body.data)) {
        configurations = mapApiResponse(response.body.data);
      } else {
        console.warn('Unexpected search response format:', response.body);
      }
      dispatch(searchConfigurationsSuccess({ configurations }));
      return configurations;
    } catch (error) {
      console.error('Error searching configurations:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseBody: error.response?.body,
        responseText: error.response?.text,
      });

      const errorMessage = error.response?.body?.message ||
                          error.response?.body?.error ||
                          error.response?.text ||
                          error.message ||
                          'Failed to search configurations';

      dispatch(searchConfigurationsFail(errorMessage));
      throw error;
    }
  };
}

export function getCategoriesStart() {
  return { type: types.GET_CATEGORIES_START };
}

export function getCategoriesSuccess(categories) {
  return { type: types.GET_CATEGORIES_SUCCESS, categories };
}

export function getCategoriesFail(error) {
  return { type: types.GET_CATEGORIES_FAIL, error };
}

export function getCategories() {
  return async (dispatch) => {
    try {
      const response = await request
        .get(CATEGORIES_ENDPOINT)
        .withCredentials();
      dispatch(getCategoriesStart());
      let categories = [];
      if (response.body && Array.isArray(response.body)) {
        categories = response.body;
      } else if (response.body?.categories && Array.isArray(response.body.categories)) {
        categories = response.body.categories;
      } else if (response.body?.data && Array.isArray(response.body.data)) {
        categories = response.body.data;
      }
      dispatch(getCategoriesSuccess(categories));
      return categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseBody: error.response?.body,
        responseText: error.response?.text,
      });
      dispatch(getCategoriesFail(error));
      throw error;
    }
  };
}
